import React, { useEffect, useRef } from 'react';

const DoctorDetailsModal = ({ doctor, isOpen, onClose }) => {
  const modalRef = useRef(null);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !doctor) return null;

  // Helper function to check if a privacy setting allows public display
  // Returns true if the setting is public (true) or undefined (default to public)
  // Returns false only if explicitly set to private (false)
  const isPubliclyVisible = (privacySetting) => {
    return privacySetting !== false;
  };

  // Helper function to format availability
  const formatAvailability = (availability) => {
    if (!availability) return [];
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const availableDays = [];
    
    days.forEach(day => {
      if (availability[day]?.available) {
        const dayName = day.charAt(0).toUpperCase() + day.slice(1);
        const slots = availability[day].slots || [];
        
        if (slots.length > 0) {
          slots.forEach(slot => {
            availableDays.push(`${dayName}: ${slot.startTime} - ${slot.endTime}`);
          });
        } else {
          availableDays.push(dayName);
        }
      }
    });
    
    return availableDays;
  };

  return (
    <div className="doctor-details-modal-overlay">
      <div className="doctor-details-modal-container" ref={modalRef}>
        <button className="doctor-details-modal-close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path 
              d="M18 6L6 18M6 6l12 12" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="doctor-details-modal-content">
          {/* Doctor Header - Always visible */}
          <div className="doctor-details-header">
            <div className="doctor-details-photo">
              {/* Profile Picture - Check privacy setting */}
              {isPubliclyVisible(doctor.profilePrivacy?.profilePicture) && (doctor.photo || doctor.profilePicture) ? (
                <img 
                  src={doctor.photo || doctor.profilePicture} 
                  alt={`${doctor.name}`}
                  className="doctor-details-photo-img"
                />
              ) : (
                <div className="doctor-details-photo-placeholder">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" 
                      stroke="#666" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="7" r="4" stroke="#666" strokeWidth="2"/>
                  </svg>
                </div>
              )}
            </div>
            <div className="doctor-details-header-info">
              {/* Basic name info - Check personal info privacy */}
              {isPubliclyVisible(doctor.profilePrivacy?.personalInfo) ? (
                <h2 className="doctor-details-name">
                  {doctor.name || `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim()}
                </h2>
              ) : (
                <h2 className="doctor-details-name">Doctor Profile</h2>
              )}
              
              {/* Professional info - Check professional info privacy */}
              {isPubliclyVisible(doctor.profilePrivacy?.professionalInfo) && (
                <>
                  {doctor.title && (
                    <p className="doctor-details-title">{doctor.title}</p>
                  )}
                  <p className="doctor-details-specialty">
                    {doctor.specialties?.join(', ') || doctor.specialty || 'General Practice'}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* About the Doctor - Check bio privacy */}
          {isPubliclyVisible(doctor.profilePrivacy?.bio) && (doctor.bio || doctor.description || doctor.experience || doctor.areasOfExpertise) && (
            <section className="doctor-details-section">
              <h3 className="doctor-details-section-title">About the Doctor</h3>
              <div className="doctor-details-about">
                {doctor.bio && (
                  <p className="doctor-details-description">{doctor.bio}</p>
                )}
                {doctor.description && !doctor.bio && (
                  <p className="doctor-details-description">{doctor.description}</p>
                )}
                
                {/* Professional info within bio section */}
                {isPubliclyVisible(doctor.profilePrivacy?.professionalInfo) && (
                  <>
                    {doctor.experience && (
                      <div className="doctor-details-info-item">
                        <h4 className="doctor-details-info-label">Experience</h4>
                        <p className="doctor-details-info-value">{doctor.experience}</p>
                      </div>
                    )}
                    {doctor.areasOfExpertise && (
                      <div className="doctor-details-info-item">
                        <h4 className="doctor-details-info-label">Areas of Expertise</h4>
                        <p className="doctor-details-info-value">{doctor.areasOfExpertise}</p>
                      </div>
                    )}
                    {doctor.yearsOfPractice && (
                      <div className="doctor-details-info-item">
                        <h4 className="doctor-details-info-label">Years of Practice</h4>
                        <p className="doctor-details-info-value">{doctor.yearsOfPractice} years</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          )}

          {/* Personal Information - Check personal info privacy */}
          {isPubliclyVisible(doctor.profilePrivacy?.personalInfo) && (
            <section className="doctor-details-section">
              <h3 className="doctor-details-section-title">Personal Information</h3>
              <div className="doctor-details-info-grid">
                {doctor.firstName && (
                  <div className="doctor-details-info-item">
                    <h4 className="doctor-details-info-label">First Name</h4>
                    <p className="doctor-details-info-value">{doctor.firstName}</p>
                  </div>
                )}
                
                {doctor.lastName && (
                  <div className="doctor-details-info-item">
                    <h4 className="doctor-details-info-label">Last Name</h4>
                    <p className="doctor-details-info-value">{doctor.lastName}</p>
                  </div>
                )}
                
                {/* Contact info - Check contact info privacy */}
                {isPubliclyVisible(doctor.profilePrivacy?.contactInfo) && (
                  <>
                    {doctor.email && (
                      <div className="doctor-details-info-item">
                        <h4 className="doctor-details-info-label">Email Address</h4>
                        <p className="doctor-details-info-value">{doctor.email}</p>
                      </div>
                    )}
                    
                    {doctor.personalContactNumber && (
                      <div className="doctor-details-info-item">
                        <h4 className="doctor-details-info-label">Personal Contact Number</h4>
                        <p className="doctor-details-info-value">{doctor.personalContactNumber}</p>
                      </div>
                    )}
                  </>
                )}
                
                {doctor.clinicLocation && (
                  <div className="doctor-details-info-item">
                    <h4 className="doctor-details-info-label">Clinic Location</h4>
                    <p className="doctor-details-info-value">{doctor.clinicLocation}</p>
                  </div>
                )}
                
                {/* Clinic contact - Check contact info privacy */}
                {isPubliclyVisible(doctor.profilePrivacy?.contactInfo) && (doctor.clinicContactNumber || doctor.clinicAddress) && (
                  <div className="doctor-details-info-item">
                    <h4 className="doctor-details-info-label">Clinic Contact</h4>
                    <div className="doctor-details-clinic-contact">
                      {doctor.clinicContactNumber && (
                        <p className="doctor-details-info-value">📞 {doctor.clinicContactNumber}</p>
                      )}
                      {doctor.clinicAddress && (
                        <p className="doctor-details-info-value">📍 {doctor.clinicAddress}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Availability - Check availability privacy */}
          {isPubliclyVisible(doctor.profilePrivacy?.availability) && (
            <section className="doctor-details-section">
              <h3 className="doctor-details-section-title">Availability</h3>
              <div className="doctor-details-availability">
                {/* Time and Date Availability */}
                {doctor.availability && (
                  <div className="doctor-details-info-item">
                    <h4 className="doctor-details-info-label">Schedule</h4>
                    <div className="doctor-details-schedule-list">
                      {formatAvailability(doctor.availability).length > 0 ? (
                        formatAvailability(doctor.availability).map((schedule, index) => (
                          <span key={index} className="doctor-details-schedule-item">
                            {schedule}
                          </span>
                        ))
                      ) : (
                        <p className="doctor-details-info-value">Schedule available upon request</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Session Types */}
                <div className="doctor-details-info-item">
                  <h4 className="doctor-details-info-label">Session Types</h4>
                  <div className="doctor-details-session-types">
                    {doctor.telehealth && (
                      <span className="doctor-details-session-type available">
                        💻 Telehealth Available
                      </span>
                    )}
                    {doctor.inPerson && (
                      <span className="doctor-details-session-type available">
                        🏥 In-Person Available
                      </span>
                    )}
                    {!doctor.telehealth && !doctor.inPerson && (
                      <span className="doctor-details-session-type">
                        Contact for session options
                      </span>
                    )}
                  </div>
                </div>

                {/* Consultation Fee */}
                {doctor.consultationFee && (
                  <div className="doctor-details-info-item">
                    <h4 className="doctor-details-info-label">Consultation Fee</h4>
                    <p className="doctor-details-consultation-fee">₱{doctor.consultationFee}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Credentials - Check credentials privacy */}
          {isPubliclyVisible(doctor.profilePrivacy?.credentials) && (
            <section className="doctor-details-section">
              <h3 className="doctor-details-section-title">Credentials</h3>
              <div className="doctor-details-credentials">
                
                {/* Education - Check education privacy */}
                {isPubliclyVisible(doctor.profilePrivacy?.education) && doctor.education && doctor.education.length > 0 && (
                  <div className="doctor-details-credential-group">
                    <h4 className="doctor-details-credential-title">Education</h4>
                    <div className="doctor-details-credential-list">
                      {doctor.education.map((edu, index) => (
                        <div key={index} className="doctor-details-credential-item">
                          <div className="doctor-details-credential-content">
                            <h5 className="doctor-details-credential-name">{edu.degree}</h5>
                            <p className="doctor-details-credential-institution">{edu.institution}</p>
                            {edu.year && (
                              <p className="doctor-details-credential-year">Year: {edu.year}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Licenses - Check license privacy */}
                {isPubliclyVisible(doctor.profilePrivacy?.license) && ((doctor.licenses && doctor.licenses.length > 0) || doctor.licenseNumber) && (
                  <div className="doctor-details-credential-group">
                    <h4 className="doctor-details-credential-title">Licenses</h4>
                    <div className="doctor-details-credential-list">
                      {/* New structured licenses */}
                      {doctor.licenses && doctor.licenses.map((license, index) => (
                        <div key={index} className="doctor-details-credential-item">
                          <div className="doctor-details-credential-content">
                            <h5 className="doctor-details-credential-name">{license.degree}</h5>
                            <p className="doctor-details-credential-number">License #: {license.licenseNumber}</p>
                            {license.expirationDate && (
                              <p className="doctor-details-credential-expiry">Expires: {license.expirationDate}</p>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Legacy license fields */}
                      {doctor.licenseNumber && (
                        <div className="doctor-details-credential-item">
                          <div className="doctor-details-credential-content">
                            <h5 className="doctor-details-credential-name">Professional License</h5>
                            <p className="doctor-details-credential-number">License #: {doctor.licenseNumber}</p>
                            {doctor.licenseIssuingAuthority && (
                              <p className="doctor-details-credential-authority">
                                Issued by: {doctor.licenseIssuingAuthority}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Certifications - Check credentials privacy (using main credentials setting) */}
                {doctor.certifications && doctor.certifications.length > 0 && (
                  <div className="doctor-details-credential-group">
                    <h4 className="doctor-details-credential-title">Certifications</h4>
                    <div className="doctor-details-credential-list">
                      {doctor.certifications.map((cert, index) => (
                        <div key={index} className="doctor-details-credential-item">
                          <div className="doctor-details-credential-content">
                            <h5 className="doctor-details-credential-name">{cert.degree}</h5>
                            <p className="doctor-details-credential-authority">{cert.issuingAuthority}</p>
                            {cert.year && (
                              <p className="doctor-details-credential-year">Year: {cert.year}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show message if no credentials are visible due to privacy settings */}
                {(!isPubliclyVisible(doctor.profilePrivacy?.education) && 
                  !isPubliclyVisible(doctor.profilePrivacy?.license) && 
                  !isPubliclyVisible(doctor.profilePrivacy?.credentials)) ? (
                  <div className="doctor-details-no-credentials">
                    <p>Credential information is not publicly available.</p>
                  </div>
                ) : (
                  // Show default message if no credentials exist but privacy allows showing them
                  (!doctor.education?.length && !doctor.licenses?.length && !doctor.licenseNumber && !doctor.certifications?.length) && (
                    <div className="doctor-details-no-credentials">
                      <p>Credential information will be updated soon.</p>
                    </div>
                  )
                )}
              </div>
            </section>
          )}

          {/* Action Buttons - Always visible */}
          <div className="doctor-details-actions">
            <button className="doctor-details-book-btn">
              <span>Book Appointment</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path 
                  d="M3.33325 10H16.6666" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M10.8333 4.16667L16.6666 10L10.8333 15.8333" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            
            {/* Contact button - Only show if contact info is public */}
            {isPubliclyVisible(doctor.profilePrivacy?.contactInfo) && (
              <button className="doctor-details-contact-btn">
                <span>Contact Doctor</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path 
                    d="M3.33341 3.33333H16.6667C17.5834 3.33333 18.3334 4.08333 18.3334 5V15C18.3334 15.9167 17.5834 16.6667 16.6667 16.6667H3.33341C2.41675 16.6667 1.66675 15.9167 1.66675 15V5C1.66675 4.08333 2.41675 3.33333 3.33341 3.33333Z" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <path 
                    d="M18.3334 5L10.0001 10.8333L1.66675 5" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDetailsModal;